import React, { useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactECharts from 'echarts-for-react';
import { theme } from '../../Utils/theme';
import '../../Styles/ChartZoomModal.css';

/**
 * ChartZoomModal – a fullscreen-style modal that renders an ECharts chart
 * at maximum viewport size while preserving every toolbox feature except
 * the zoom (fullscreen) trigger itself, which is shown disabled.
 *
 * Designed as a reusable, self-contained component that can be dropped into
 * any analytics chart viewer.  BaseChart uses it internally so every chart
 * gets zoom support for free.
 */
const ChartZoomModal = React.memo(function ChartZoomModal({
  open,
  onClose,
  option,
  title,
  subtitle,
}) {
  // Close on Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  const zoomedOption = useMemo(() => {
    if (!option) return {};

    const toolbox = option.toolbox ? { ...option.toolbox } : {};
    const feature = toolbox.feature ? { ...toolbox.feature } : {};

    const { myFullscreen, ...restFeatures } = feature;

    toolbox.feature = restFeatures;

    return {
      ...option,
      toolbox,
      title: { show: false },
      // Give the zoomed chart a generous grid so labels aren't clipped
      backgroundColor: theme.chartZoomTrackBg(),
      animationDuration: 600,
      animationEasing: 'cubicOut',
    };
  }, [option]);

  if (!open) return null;

  return createPortal(
    <div
      className="chart-zoom-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="chart-zoom-container" role="dialog" aria-modal="true" aria-label={title || 'Chart zoom'}>
        {/* Header */}
        <div className="chart-zoom-header">
          <div className="chart-zoom-header-text">
            {title && <h5 className="fw-bold mb-0">{title}</h5>}
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="chart-zoom-close"
              onClick={onClose}
              aria-label="Close zoom"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Chart body */}
        <div className="chart-zoom-body">
          <ReactECharts
            option={zoomedOption}
            style={{ height: '100%', width: '100%' }}
            notMerge
            lazyUpdate
          />
        </div>
      </div>
    </div>,
    document.body
  );
});

export default ChartZoomModal;
